import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  useGetConfigQuery,
  useGetPlaceCodeLazyQuery,
  useUpdatePlaceCodeMutation,
  useGetPlaceSymbolSvgsLazyQuery,
  useGetPlaceForCodeCreateQuery,
  useGetPlaceCodesQuery,
} from '../generated/graphql';
import master from '../Master';
import { ToastContext } from '../components/Toast';
import { Loading } from '../Loading';
import { Input } from '../components/Input';
import { FormActions } from '../components/FormActions';
import { SectionHeader } from '../components/SectionHeader';
import { PlaceCodeShapeInput } from '../components/create-place-code/PlaceCodeShapeInput';
import {
  IInputField,
  InputFieldsInput,
} from '../components/create-place-code/InputFieldsInput';
import {
  IAdditionalField,
  AdditoinalFieldsInput,
} from '../components/create-place-code/AdditoinalFieldsInput';
import {
  IOutputField,
  OutputFieldsInput,
} from '../components/create-place-code/OutputFieldsInput';
import {
  FieldType,
  IDrawingObject,
  DrawingObjectsInput,
  findMissingInputFieldNames,
  findMissingInputFieldRowIndexes,
} from '../components/create-place-code/DrawingObjectsInput';
import {
  GraphQLErrorDialog,
  NotifyDialog,
} from '../components/Dialogs';
import './PlaceCreateCodePage.css';
import { useValidation } from '../utils/Validation';
import { ValidationRules, uniqueNameValidator } from '../utils/ValidationRules';
import {
  PlaceCodeEverguardInput,
  convertEverguardThresholdText,
  getEverguardThresholdError,
  shapeUsesEverguard,
  toEverguardThresholdText,
  toStoredEverguardThresholdFromText,
} from '../components/create-place-code/EverguardSettingInput';
import { PlaceCodeDisplayUnitInput } from '../components/create-place-code/PlaceCodeDisplayUnitInput';
import {
  DEFAULT_LENGTH_DISPLAY_UNIT,
  DEFAULT_AREA_DISPLAY_UNIT,
  getInputFieldUnitInputValue,
  getStoredInputFieldUnit,
  resetInputFieldUnits,
} from '../utils/PlaceCodeDisplayUnits';

type IPlaceCreateCodePageProps = RouteComponentProps<{ placeId: string }> & {
  existingId?: number;
};

/** 補修コードの画層。画面では編集しないが、保存時に既存の値をそのまま送る */
interface ICodeLayer {
  name: string;
}

const allInputFields = master.InputFields.map((fieldMaster) => ({
  input_field_scheme_id: fieldMaster.id,
  unit: getInputFieldUnitInputValue(fieldMaster.unitKind, fieldMaster.defaultUnit),
  export: false,
  master: fieldMaster,
}));

/** 新規の登録か既存の編集かで、見出し・送信ボタン・成功の通知の文言を出し分ける */
function getFormTexts(existingId: number | undefined) {
  if (existingId) {
    return {
      title: '補修コードの編集',
      submitLabel: '保存',
      completedMessage: '補修コードを保存しました',
    };
  }
  return {
    title: '補修コードの登録',
    submitLabel: '登録',
    completedMessage: '補修コードを登録しました',
  };
}

/**
 * 表の入力チェックで誤りが見つかったときに、誤りのある行の位置を返す。
 * 表全体の入力チェック（validate）を1行ずつに当てて、誤りを返した行を集める。
 */
function getInvalidRowIndexes<T>(
  fields: T[],
  validate: (fields: T[]) => string | undefined,
  message: string | undefined
): number[] {
  if (!message) {
    return [];
  }
  const indexes: number[] = [];
  fields.forEach((field, index) => {
    if (validate([field])) {
      indexes.push(index);
    }
  });
  return indexes;
}

/** 図形の種類が剥落防止の設定をできるときだけ、浮き面積の基準の入力の誤りを返す */
function getEverguardError(shapeSchemeId: number, thresholdText: string | undefined): string | undefined {
  if (!shapeUsesEverguard(shapeSchemeId)) {
    return undefined;
  }
  return getEverguardThresholdError(thresholdText);
}

/**
 * 作画文字列が、図形の種類にない自動計測項目を使っているときに、図形の種類の欄の下へ出す誤り。
 * 図形の種類を変えたあとでも、作画文字列を直すまで保存できないようにする
 */
function getShapeFieldMismatchError(shapeName: string, missingFieldNames: string[]): string | undefined {
  if (missingFieldNames.length === 0) {
    return undefined;
  }
  const fieldNames = missingFieldNames.map((name) => `「${name}」`).join('');
  return `図面上に作画する文字列で使っている${fieldNames}は、「${shapeName}」にはない項目です。作画文字列の赤い行を削除するか、項目名を変更してください。`;
}

/** 図面上に作画する文字列の表の下へ出す誤り。表そのものの入力の誤りを先に出す */
function getDrawingObjectsError(validationMessage: string | undefined, shapeName: string, hasMissingField: boolean): string | undefined {
  if (validationMessage) {
    return validationMessage;
  }
  if (hasMissingField) {
    return `赤い行の自動計測項目は、図形の種類「${shapeName}」にはありません。行を削除するか、項目名を変更してください。`;
  }
  return undefined;
}

// 初期値の取得元をクエリ文字列で受け取る。読めない値は指定なしと同じ扱いにする
function parseCopySourceId(search: string): number | undefined {
  const rawValue = new URLSearchParams(search).get('copyFrom');
  if (rawValue === null) {
    return undefined;
  }

  const copySourceId = parseInt(rawValue);
  if (Number.isNaN(copySourceId)) {
    return undefined;
  }
  return copySourceId;
}

// 新規作成では初期値の名称がコピー元と同名になり、同名登録の禁止で保存できないため区別を付ける
function inheritName(name: string, existingId: number | undefined): string {
  if (existingId === undefined) {
    return `${name}のコピー`;
  }
  return name;
}

// 新規作成では初期値として読み込んだ項目も新しい項目として登録させるため、既存の識別子を外す
function inheritFieldId(fieldId: number | undefined, existingId: number | undefined): number | undefined {
  if (existingId === undefined) {
    return undefined;
  }
  return fieldId;
}

export const PlaceCreateCodePage: React.FC<IPlaceCreateCodePageProps> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const sourceCodeId = props.existingId ?? parseCopySourceId(props.location.search);

  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const placeCodesResult = useGetPlaceCodesQuery({
    variables: { placeId },
    fetchPolicy: 'network-only',
  });
  // 編集中の補修コード自身は同名判定の対象から外す
  const otherCodeNames = (placeCodesResult.data?.getPlace.codes ?? [])
    .filter((code) => code.id !== props.existingId)
    .map((code) => code.name);
  const validation = useValidation({
    ...ValidationRules.PlaceCode,
    name: uniqueNameValidator(otherCodeNames),
  });
  const formTexts = getFormTexts(props.existingId);
  const listPath = `/places/${placeId}/codes`;

  const [name, setName] = validation.useState('name', '');
  const [lengthDisplayUnit, setLengthDisplayUnit] = React.useState<number>(DEFAULT_LENGTH_DISPLAY_UNIT);
  const [areaDisplayUnit, setAreaDisplayUnit] = React.useState<number>(DEFAULT_AREA_DISPLAY_UNIT);
  const [work, setWork] = React.useState<number>();
  const [shape, setShape] = React.useState(1);
  const [shapeSymbol, setShapeSymbol] = React.useState<number | null>(1);
  const [everguardThresholdText, setEverguardThresholdText] = React.useState<string>();
  const [layers, setLayers] = React.useState<ICodeLayer[]>([]);
  const [inputFields, setInputFields] = validation.useState<IInputField[]>(
    'inputFields',
    allInputFields,
    true
  );
  const [additionalFields, setAdditionalFields] = validation.useState<
    IAdditionalField[]
  >('additionalFields', [], true);
  const [outputFields, setOutputFields] = validation.useState<IOutputField[]>(
    'outputFields',
    [],
    true
  );
  const [drawingObjects, setDrawingObjects] = validation.useState<
    IDrawingObject[]
  >('drawingObjects', [], true);

  const shapeMaster = master.Shapes.find((s) => s.id === shape);
  if (!shapeMaster) {
    throw new Error('invalid shape id');
  }

  const [hasReferenceError, setHasReferenceError] = React.useState(false);

  // 図形の種類に応じた入力項目
  const shapeInputFields = shapeMaster.input_field_ids
    .map((id) => inputFields.find((field) => field.input_field_scheme_id === id))
    .filter((field): field is IInputField => !!field);

  const getConfigResult = useGetConfigQuery();

  const [getPlaceSymbolSvgs, getPlaceSymbolSvgsResult] = useGetPlaceSymbolSvgsLazyQuery();

  const getPlaceForCodeCreateResult = useGetPlaceForCodeCreateQuery({ variables: { placeId } });

  const symbolSvgs = (getPlaceSymbolSvgsResult.data?.getPlaceSymbolSvgs ?? []).map(s => ({
    id: s.id,
    name: s.name,
  }));

  const [getPlaceCode, getPlaceCodeResult] = useGetPlaceCodeLazyQuery({
    onCompleted(data) {
      const code = data.getPlaceCode;
      setName(inheritName(code.name, props.existingId));
      setLengthDisplayUnit(code.length_display_unit);
      setAreaDisplayUnit(code.area_display_unit);
      setWork(code.selected_work?.id);
      setShape(code.shape_scheme_id);
      setShapeSymbol(code.shape_symbol_scheme_id ?? null);
      setLayers(code.layers);
      setEverguardThresholdText(
        toEverguardThresholdText(code.everguard_setting?.everguard_threshold, code.area_display_unit)
      );

      setInputFields(
        inputFields.map((shapeInputField) => {
          const field = code.input_fields.find(
            (f) =>
              f.input_field_scheme_id === shapeInputField.input_field_scheme_id
          );

          if (!field) {
            return shapeInputField;
          }
          return {
            input_field_scheme_id: field.input_field_scheme_id,
            unit: getInputFieldUnitInputValue(shapeInputField.master.unitKind, field.unit),
            export: field.export_to_summary,
            master: shapeInputField.master,
          };
        })
      );
      setAdditionalFields(
        code.additional_fields.map((f) => ({
          id: inheritFieldId(f.id, props.existingId),
          name: f.name,
          unit: f.unit,
          valueType: f.value_type,
          options: f.options,
          export: f.export_to_summary,
        }))
      );
      setOutputFields(
        code.output_fields.map((f) => ({
          id: inheritFieldId(f.id, props.existingId),
          name: f.name,
          formula: f.formula,
          unit: f.unit,
          export: f.export_to_summary,
        }))
      );

      const drawingObjects = [];
      for (const obj of code.drawing_objects) {
        let fieldIndex;
        switch (obj.field_type) {
          case FieldType.InputField:
            const field = code.input_fields.find((f) => f.id === obj.field_id);
            if (!field) {
              setHasReferenceError(true);
              continue;
            }
            fieldIndex = field.input_field_scheme_id;
            break;
          case FieldType.AdditionalField:
            fieldIndex = code.additional_fields.findIndex(
              (f) => f.id === obj.field_id
            );
            break;
          case FieldType.OutputField:
            fieldIndex = code.output_fields.findIndex(
              (f) => f.id === obj.field_id
            );
            break;
          default:
            throw new Error('unknown field type');
        }

        drawingObjects.push({
          id: inheritFieldId(obj.id, props.existingId),
          fieldType: obj.field_type,
          fieldIndex,
          prefix: obj.prefix,
          suffix: obj.suffix,
          lineType: obj.leader_line_type,
          arrowEndType: obj.leader_arrow_end_type,
          digit: obj.digit,
          placeEnclosingSvgId: obj.place_enclosing_svg_id ?? null,
        });
      }

      setDrawingObjects(drawingObjects);
    },
  });

  const [addPlaceCode, addPlaceCodeResult] = useUpdatePlaceCodeMutation({
    onCompleted() {
      toastContext.showToast(formTexts.completedMessage, 'success');

      history.push(listPath);
    },
    onError() {
      toastContext.showToast('保存できませんでした。時間をおいて再実行してください。', 'error');
    },
  });

  const everguardError = getEverguardError(shape, everguardThresholdText);
  const missingFieldRowIndexes = findMissingInputFieldRowIndexes(shapeMaster.input_field_ids, drawingObjects);
  const shapeFieldError = getShapeFieldMismatchError(
    shapeMaster.name,
    findMissingInputFieldNames(shapeMaster.input_field_ids, drawingObjects)
  );

  function update() {
    const drawingObjectsData = [];
    for (const obj of drawingObjects) {
      let fieldIndex;
      if (obj.fieldType === FieldType.InputField) {
        fieldIndex = shapeInputFields.findIndex(
          (field) => field.master.id === obj.fieldIndex
        );
      } else {
        fieldIndex = obj.fieldIndex;
      }

      if (fieldIndex === -1) {
        console.warn('不正な参照を持つDrawingObjectを無視します');
        continue;
      }

      drawingObjectsData.push({
        id: obj.id,
        field_type: obj.fieldType,
        field_index: fieldIndex,
        prefix: obj.prefix,
        suffix: obj.suffix,
        leader_line_type: obj.lineType,
        leader_arrow_end_type: obj.arrowEndType,
        digit: obj.digit,
        place_enclosing_svg_id: obj.placeEnclosingSvgId,
      });
    }


    const placeCodeInput = {
      id: props.existingId,
      place_id: placeId,
      name: name,
      length_display_unit: lengthDisplayUnit,
      area_display_unit: areaDisplayUnit,
      work_id: work,
      shape_scheme_id: shape,
      shape_symbol_scheme_id: shapeSymbol,
      layers: layers.map((layer) => ({
        name: layer.name,
      })),
      input_fields: shapeInputFields.map((field) => ({
        input_field_scheme_id: field.input_field_scheme_id,
        unit: getStoredInputFieldUnit(field.master.unitKind, field.unit),
        export: field.export,
      })),
      additional_fields: additionalFields.map((field) => ({
        id: field.id,
        name: field.name,
        unit: field.unit,
        export: field.export,
        value_type: field.valueType,
        options: field.options,
      })),
      output_fields: outputFields.map((field) => ({
        id: field.id,
        name: field.name,
        formula: field.formula,
        unit: field.unit,
        export: field.export,
      })),
      drawing_objects: drawingObjectsData,
    };

    const everguardThreshold = toStoredEverguardThresholdFromText(everguardThresholdText, areaDisplayUnit);
    if (everguardThreshold !== undefined) {
      addPlaceCode({
        variables: {
          input: {
            ...placeCodeInput,
            everguard_setting: {
              everguard_threshold: everguardThreshold,
            }
          }
        },
      });
    } else {
      addPlaceCode({
        variables: {
          input: placeCodeInput
        },
      });
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////

  // 同名判定に既存の補修コード一覧が要るため、読み込みを待ってから既存データを取得する
  if (placeCodesResult.error) {
    return <GraphQLErrorDialog error={placeCodesResult.error} />;
  }

  if (placeCodesResult.loading || !placeCodesResult.data) {
    return <Loading />;
  }

  // 変更では対象の補修コードを、新規作成では初期値の取得元を読み込む
  if (sourceCodeId !== undefined && !getPlaceCodeResult.called) {
    getPlaceCode({
      variables: {
        placeId,
        placeCodeId: sourceCodeId,
      },
    });
  }

  // シンボルSVGを取得する
  if (!getPlaceSymbolSvgsResult.called) {
    getPlaceSymbolSvgs({
      variables: {
        placeId,
      },
    });
  }

  if (getConfigResult.error) {
    return <GraphQLErrorDialog error={getConfigResult.error} />;
  }

  if (getConfigResult.loading || !getConfigResult.data) {
    return <Loading />;
  }

  if (getPlaceCodeResult.called) {
    if (getPlaceCodeResult.error) {
      return <GraphQLErrorDialog error={getPlaceCodeResult.error} />;
    }

    if (getPlaceCodeResult.loading || !getPlaceCodeResult.data) {
      return <Loading />;
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////

  const additionalFieldsMessage = validation.get('additionalFields');
  const outputFieldsMessage = validation.get('outputFields');

  return (
    <div className="PlaceCreateCodePage-container">
      <Loading open={addPlaceCodeResult.loading} />
      <NotifyDialog
        open={hasReferenceError}
        title="不正なデータ"
        message={<p>不正な参照を持つ図面上に作画する文字列があります（再度保存すると修正されます）。</p>}
        onYes={() => setHasReferenceError(false)}
      />
      <SectionHeader title={formTexts.title} />
      <div className="PlaceCreateCodePage-form">
        <section className="PlaceCreateCodePage-section">
          <h3 className="PlaceCreateCodePage-section_title">基本情報</h3>
          <div className="PlaceCreateCodePage-field">
            <Input
              label="コード名"
              required={true}
              value={name}
              validationMessage={validation.get('name')}
              onChange={(event) => setName(event.target.value)}
            />
            <p className="PlaceCreateCodePage-help">
              作業者にわかりやすい名称を入力してください（例: 「ひびわれ0.5mm」「タイル浮き」「白華現象」）。
            </p>
          </div>
          <PlaceCodeShapeInput
            tiles={getConfigResult.data.getConfig.tiles}
            symbolSvgs={symbolSvgs}
            shapeSchemeId={shape}
            shapeSymbolSchemeId={shapeSymbol}
            onChangeShape={setShape}
            validationMessage={shapeFieldError}
            onChangeShapeSymbol={setShapeSymbol}
          />
          <PlaceCodeDisplayUnitInput
            lengthDisplayUnit={lengthDisplayUnit}
            areaDisplayUnit={areaDisplayUnit}
            onChangeLengthDisplayUnit={(value) => {
              if (value !== lengthDisplayUnit) {
                setLengthDisplayUnit(value);
                setInputFields((fields) => resetInputFieldUnits(fields, 'length'));
              }
            }}
            onChangeAreaDisplayUnit={(value) => {
              if (value !== areaDisplayUnit) {
                setAreaDisplayUnit(value);
                setInputFields((fields) => resetInputFieldUnits(fields, 'area'));
                // 浮き面積の基準は面積の表示単位で入力するため、同じ面積を表す新しい単位の値に置き換える
                setEverguardThresholdText(
                  convertEverguardThresholdText(everguardThresholdText, areaDisplayUnit, value)
                );
              }
            }}
          />
          <PlaceCodeEverguardInput
            shapeSchemeId={shape}
            areaDisplayUnit={areaDisplayUnit}
            thresholdText={everguardThresholdText}
            validationMessage={everguardError}
            onChange={setEverguardThresholdText}
          />
        </section>

        <section className="PlaceCreateCodePage-section">
          <h3 className="PlaceCreateCodePage-section_title">施工管理</h3>
          <label className="App-label" htmlFor="PlaceCreateCodePage-work">工種</label>
          <select
            id="PlaceCreateCodePage-work"
            value={work ?? 0}
            onChange={(event) => {
              const nextWorkId = parseInt(event.target.value);
              if (nextWorkId === 0) {
                setWork(undefined);
              } else {
                setWork(nextWorkId);
              }
            }}
          >
            <option key={0} value={0}>施工管理なし</option>
            {getPlaceForCodeCreateResult.data?.getPlace.works.map(work =>
              <option key={work.id} value={work.id}>{work.name}</option>)}
          </select>
        </section>

        <section className="PlaceCreateCodePage-section">
          <h3 className="PlaceCreateCodePage-section_title">自動計測項目</h3>
          <p className="PlaceCreateCodePage-help">
            図形登録時に自動的に計測される項目です。「集計表に出力」にチェックを入れると、数量表1ページ目の「集計表」に積算された結果が記載されます。
          </p>
          <InputFieldsInput
            value={shapeInputFields}
            validation={validation.get('inputFields')}
            lengthDisplayUnit={lengthDisplayUnit}
            areaDisplayUnit={areaDisplayUnit}
            onChange={(fields) =>
              setInputFields((inputFields) =>
                inputFields.map((field) => {
                  let updated = fields.find(
                    (f) => f.input_field_scheme_id === field.input_field_scheme_id
                  );
                  return updated ?? field;
                })
              )
            }
          />
        </section>

        <section className="PlaceCreateCodePage-section">
          <h3 className="PlaceCreateCodePage-section_title">手動入力項目</h3>
          <p className="PlaceCreateCodePage-help">
            図形登録時に作業者が手入力する項目です。種別や特殊な情報などを追加で入力してほしい場合に使用します。「集計表に出力」にチェックを入れると、数量表1ページ目の「集計表」に積算された結果が記載されます。
          </p>
          <AdditoinalFieldsInput
            value={additionalFields}
            drawingObjects={drawingObjects}
            validation={additionalFieldsMessage}
            invalidRowIndexes={getInvalidRowIndexes(
              additionalFields,
              ValidationRules.PlaceCode.additionalFields,
              additionalFieldsMessage
            )}
            onChange={(fields) => setAdditionalFields(fields)}
            onDrawingObjectsChange={(objs) => setDrawingObjects(objs)}
          />
        </section>

        <section className="PlaceCreateCodePage-section">
          <h3 className="PlaceCreateCodePage-section_title">計算結果項目</h3>
          <p className="PlaceCreateCodePage-help">
            「自動計測項目」「手動入力項目」を使って計算した値が代入される項目です（例:「長さ(横) * 1.2」「面積 / 120」）。長さは mm 単位で、面積は m2 単位で渡されます。「集計表に出力」にチェックを入れると、数量表1ページ目の「集計表」に積算された結果が記載されます。
          </p>
          <OutputFieldsInput
            value={outputFields}
            drawingObjects={drawingObjects}
            validation={outputFieldsMessage}
            invalidRowIndexes={getInvalidRowIndexes(
              outputFields,
              ValidationRules.PlaceCode.outputFields,
              outputFieldsMessage
            )}
            onChange={(fields) => setOutputFields(fields)}
            onDrawingObjectsChange={(objs) => setDrawingObjects(objs)}
          />
        </section>

        <section className="PlaceCreateCodePage-section">
          <h3 className="PlaceCreateCodePage-section_title">図面上に作画する文字列</h3>
          <DrawingObjectsInput
            value={drawingObjects}
            validation={getDrawingObjectsError(validation.get('drawingObjects'), shapeMaster.name, missingFieldRowIndexes.length > 0)}
            invalidRowIndexes={missingFieldRowIndexes}
            inputFields={shapeInputFields}
            additionalFields={additionalFields}
            outputFields={outputFields}
            enclosingSvgs={getPlaceForCodeCreateResult.data?.getPlace.enclosing_svgs.map(s => ({ id: s.id, svg: s.svg })) ?? []}
            onChange={(objs) => setDrawingObjects(objs)}
          />
        </section>

        <div className="PlaceCreateCodePage-actions">
          <FormActions
            submitLabel={formTexts.submitLabel}
            submitDisabled={
              validation.isInvalid || everguardError !== undefined || shapeFieldError !== undefined || addPlaceCodeResult.loading
            }
            onCancel={() => history.push(listPath)}
            onSubmit={update}
          />
        </div>
      </div>
    </div>
  );
};
